import { Button, Section, withSkeletonLoading } from "@landtechnologies/components";
import { SavingIndicator, useSavingState } from "./SavingIndicator";
import { Stores, useGlobalState, useTranslation } from "react-migration/lib";
import { useCallback, useEffect } from "react";

import { DeleteSiteButton } from "./DeleteSiteButton";
import { EditableSiteBoundary } from "./EditableSiteBoundary";
import { EditableSiteDescription } from "./EditableSiteDescription";
import { EditableSiteLabels } from "./EditableSiteLabels";
import { EditableSiteTitle } from "./EditableSiteTitle";
import { Feature } from "src/js/stores/user";
import { GeometryMeasurements } from "react-migration/components/GeometryMeasurements";
import { ReportSection } from "./ReportSection";
import { ReportSidebar } from "./ReportSidebar";
import { ShareSiteButton } from "../ShareSiteButton";
import { SiteReportUtilitySteps } from "../../typings/SiteUtilityReport";
import { StageSelect } from "../SaveSiteButton/StageSelect";
import { hasBetaFeature } from "src/js/stores/user/actions";
import { refreshSiteGeometryStore } from "src/js/stores/siteGeometry/actions";
import { router } from "src/js/router/react-router";
import { setActiveSiteReportUtilityStep } from "src/js/stores/site/actions/setActiveSiteReportUtilityStep";
import { useSiteById } from "react-migration/domains/sites/hooks/useSiteById";
import { useSiteStages } from "../../hooks";
import { useUpdateSiteMutation } from "../SaveSiteButton/useUpdateSiteMutation";

interface SiteDetailsProps {
  siteId: string;
  onDelete?(): void;
}

export const EditableSiteDetails = withSkeletonLoading<SiteDetailsProps>(
  ({ hideSkeletonLoading, siteId, onDelete }) => {
    const { t } = useTranslation();
    const { saving, startedSaving, stoppedSaving } = useSavingState();
    const { site, loading } = useSiteById(siteId);
    const { stages, ...stagesQuery } = useSiteStages();
    const [updateSite] = useUpdateSiteMutation();
    const [
      {
        googleMap,
        settings: { showSiteUtilityReport },
      },
      { updateMapSettings },
    ] = useGlobalState(Stores.map);

    const handleShowSiteUtilityReport = useCallback(
      (isShown: boolean) => {
        updateMapSettings("showSiteUtilityReport", isShown);
        if (!isShown) {
          setActiveSiteReportUtilityStep(SiteReportUtilitySteps.Overview);
        }
      },
      [updateMapSettings]
    );

    useEffect(() => {
      return () => handleShowSiteUtilityReport(false);
    }, [handleShowSiteUtilityReport]);

    useEffect(() => {
      if (!loading) {
        hideSkeletonLoading();
      }
    }, [hideSkeletonLoading, loading]);

    const onSaveComplete = useCallback(() => {
      stoppedSaving();
      googleMap && refreshSiteGeometryStore(googleMap);
    }, [googleMap, stoppedSaving]);

    if (!site) return <div className="atlas-h-[220px]" />;

    return (
      <Section
        data-testid="site-details"
        bodyClasses="atlas-p-3 atlas-flex atlas-flex-col atlas-gap-6"
      >
        <div className="atlas-flex atlas-justify-between atlas-items-center">
          <h1 className="atlas-text-base atlas-font-extralight atlas-text-content-secondary">
            {t("sites.card.site_details.heading")}
          </h1>
          <div className="atlas-flex atlas-justify-end atlas-gap-2.5">
            <ShareSiteButton siteId={siteId} title={t("sidebar.site.share")} />
            <Button.Secondary
              size="small"
              onClick={() => {
                router.push({ name: "sites", query: { site_id: siteId } });
              }}
            >
              {t("sidebar.site.view_site")}
            </Button.Secondary>
            <DeleteSiteButton siteId={siteId} onDelete={onDelete} />
          </div>
        </div>
        <div>
          <div className="atlas-divide-y atlas-divide-neutral-200">
            <div className="atlas-pb-3">
              <GeometryMeasurements geometry={site.geometry} />
            </div>
            <div className="atlas-relative atlas-flex atlas-pt-3 atlas-flex-col atlas-gap-3 atlas-text-xs">
              <div className="atlas-flex atlas-gap-3">
                <label className="atlas-text-content-secondary atlas-font-semibold atlas-w-20 atlas-flex-shrink-0 atlas-leading-8">
                  {t("sites.card.site_card.boundary")}
                </label>
                <EditableSiteBoundary
                  site={site}
                  startedSaving={startedSaving}
                  stoppedSaving={onSaveComplete}
                />
              </div>
              <div className="atlas-flex atlas-gap-3">
                <label className="atlas-text-content-secondary atlas-font-semibold atlas-w-20 atlas-flex-shrink-0 atlas-leading-8">
                  {t("sites.card.site_card.title")}
                </label>
                <EditableSiteTitle
                  site={site}
                  startedSaving={startedSaving}
                  stoppedSaving={onSaveComplete}
                />
              </div>
              <div className="atlas-flex atlas-gap-3">
                <label className="atlas-text-content-secondary atlas-font-semibold atlas-w-20 atlas-flex-shrink-0 atlas-leading-8">
                  {t("sites.card.site_card.stage")}
                </label>
                <StageSelect
                  onChange={(value) => {
                    startedSaving();
                    updateSite({
                      variables: { updateData: { _id: site._id, _stage: value } },
                      onError: stoppedSaving,
                      onCompleted: onSaveComplete,
                    });
                  }}
                  value={site._stage._id}
                  stages={stages ?? []}
                  loading={stagesQuery.loading}
                  networkStatus={stagesQuery.networkStatus}
                />
              </div>
              <div
                className={"atlas-mb-4 atlas-flex atlas-items-start atlas-gap-3 atlas-self-strech"}
              >
                <div
                  className={
                    "label-text-content-secondary atlas-font-semibold atlas-w-20 atlas-flex-shrink-0 atlas-text-xs"
                  }
                >
                  {t("sites.card.site_card.labels")}
                </div>
                <div
                  className={
                    "atlas-flex atlas-flex-wrap atlas-flex-row atlas-w-68 atlas-gap-2 atlas-whitespace-pre-line atlas-overflow-elipsis"
                  }
                >
                  <EditableSiteLabels
                    site={site}
                    startedSaving={startedSaving}
                    stoppedSaving={stoppedSaving}
                  />
                </div>
              </div>
              <div className="atlas-flex atlas-gap-3">
                <label className="atlas-text-content-secondary atlas-font-semibold atlas-w-20 atlas-flex-shrink-0 atlas-leading-8">
                  {t("sites.card.site_card.description")}
                </label>
                <EditableSiteDescription
                  site={site}
                  startedSaving={startedSaving}
                  stoppedSaving={stoppedSaving}
                />
              </div>
              {hasBetaFeature(Feature.siteUtilityReportBeta) && (
                <ReportSection onButtonClick={() => handleShowSiteUtilityReport(true)} />
              )}
              <SavingIndicator saving={saving} />
            </div>
          </div>
        </div>
        {showSiteUtilityReport && (
          <ReportSidebar onClose={() => handleShowSiteUtilityReport(false)} />
        )}
      </Section>
    );
  },
  { rows: 3 }
);
